import { ChevronDown } from "lucide-react";
import { memo } from "react";
import type { CellState } from "@/lib/availability/cell-state";
import { windowNetHours } from "@/lib/availability/groups";
import { monthLabel } from "@/lib/availability/months";
import { cn } from "@/lib/utils";
import { GroupCell, ResourceCell, type TooltipHandlers } from "./AvailabilityCell";
import { RowChip } from "./Chips";
import { BAR_CLASS } from "./cell-style";
import { columnEdge, type GridLayout } from "./grid-layout";
import { averagePct, barRatio, type GridGroup, type ViewRow } from "./model";
import { NetBar } from "./NetBar";

/** Hours at which a person's / a group's net bar is full. */
const RESOURCE_NET_SCALE = 300;
const GROUP_NET_SCALE = 1200;

const BAR_MAX_PX = 24;

interface LastThreeItem {
  month: string;
  state: CellState;
  pct: number | null;
}

/** "Last 3 mo": three actual months as tiny bars, with the average Booked share beside them. */
function LastThree({ items }: { items: ReadonlyArray<LastThreeItem | undefined> }) {
  return (
    <div className="flex items-center gap-2 px-2">
      <div className="relative flex h-6 items-end gap-0.5">
        <span
          aria-hidden
          className="absolute -right-0.5 -left-0.5 bottom-4 border-t border-dashed border-[color:var(--avail-unplanned-border)]"
        />
        {items.map((item, index) => (
          <span
            key={item?.month ?? `missing-${index}`}
            title={
              item === undefined || item.pct === null
                ? `${item ? monthLabel(item.month, { short: true }) : "–"}: no data`
                : `${monthLabel(item.month, { short: true })}: ${Math.round(item.pct)}% of Available Hours`
            }
            className={cn("w-[7px] rounded-[1px]", item ? BAR_CLASS[item.state] : "bg-muted")}
            style={{ height: Math.max(2, barRatio(item) * BAR_MAX_PX) }}
          />
        ))}
      </div>
      <span className="font-mono text-sm text-muted-foreground">{averagePct(items)}</span>
    </div>
  );
}

interface SharedRowProps extends TooltipHandlers {
  layout: GridLayout;
  gridRow: number;
  /** The column that is this row's Tab stop, or null. */
  tabCol: number | null;
}

interface ResourceRowProps extends SharedRowProps {
  row: ViewRow;
  selectedMonth: string | null;
  onSelect: (resourceId: string, month: string) => void;
}

export const ResourceRow = memo(function ResourceRow({
  row,
  layout,
  gridRow,
  tabCol,
  selectedMonth,
  onSelect,
  onTipShow,
  onTipHide,
}: ResourceRowProps) {
  const meta = [row.homeRole?.trim() || "No Home Role", row.disciplineCode ?? "No Discipline"];
  if (row.primaryAccount !== null) meta.push(row.primaryAccount.name);
  const lastThree = layout.lastThreeMonths.map((month): LastThreeItem | undefined => {
    const cell = row.cells[month];
    return cell === undefined ? undefined : { month, state: cell.state, pct: cell.pct };
  });

  return (
    <div
      className="grid border-b border-border/60 bg-card"
      style={{ gridTemplateColumns: layout.template, columnGap: layout.columnGapPx }}
    >
      <div
        className={cn(
          "sticky left-0 z-10 flex min-w-0 flex-col justify-center gap-0.5 bg-card py-1 pr-3 pl-11",
        )}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-heading">{row.name}</span>
          {row.chronic ? <RowChip kind="chronic" /> : null}
          {row.rollingOff ? <RowChip kind="rollingOff" /> : null}
          {row.nonDelivery ? <RowChip kind="nonDelivery" /> : null}
        </div>
        {layout.compact ? null : (
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate">{meta.join(" · ")}</span>
            {row.otherAccountCount > 0 ? (
              <span
                title={`Also has Booked hours on ${row.otherAccountCount} other ${
                  row.otherAccountCount === 1 ? "Account" : "Accounts"
                } in this window`}
                className="shrink-0 rounded-full border px-1.5 text-xs"
              >
                also on {row.otherAccountCount}
              </span>
            ) : null}
          </div>
        )}
      </div>
      <NetBar netHours={windowNetHours(row, layout.windowMonths)} scale={RESOURCE_NET_SCALE} />
      {layout.lastThreeMonths.length > 0 ? <LastThree items={lastThree} /> : null}
      {layout.columns.map((month, index) => {
        const cell = row.cells[month];
        if (cell === undefined) return <div key={month} />;
        return (
          <ResourceCell
            key={month}
            row={row}
            cell={cell}
            gridRow={gridRow}
            gridCol={index}
            tabbable={tabCol === index}
            selected={selectedMonth === month}
            past={month < layout.currentMonth}
            edge={columnEdge(layout, month, index)}
            compact={layout.compact}
            onSelect={onSelect}
            onTipShow={onTipShow}
            onTipHide={onTipHide}
          />
        );
      })}
    </div>
  );
});

interface GroupRowProps extends SharedRowProps {
  group: GridGroup;
  open: boolean;
  /** The month of the selected cell in this group's header row, or null. */
  selectedMonth: string | null;
  onToggle: (groupId: string) => void;
  onSelect: (groupKey: string | null, month: string) => void;
}

export const GroupRow = memo(function GroupRow({
  group,
  open,
  layout,
  gridRow,
  tabCol,
  selectedMonth,
  onToggle,
  onSelect,
  onTipShow,
  onTipHide,
}: GroupRowProps) {
  const notes: string[] = [];
  if (group.isUnassigned) notes.push("Fix in NOS to place them");
  if (group.hiddenCount > 0) notes.push(`${group.hiddenCount} hidden by filters`);
  const lastThree = layout.lastThreeMonths.map((month, index): LastThreeItem => {
    const result = group.lastThree[index];
    return { month, state: result?.state ?? "noData", pct: result?.pct ?? null };
  });

  return (
    <div
      className={cn(
        "grid border-b",
        group.isUnassigned
          ? "bg-[color-mix(in_oklab,var(--avail-stretched)_45%,var(--card))]"
          : "bg-muted",
      )}
      style={{ gridTemplateColumns: layout.template, columnGap: layout.columnGapPx }}
    >
      <div
        className={cn(
          "sticky left-0 z-10 flex min-w-0 items-center gap-1.5 py-1.5 pr-3 pl-2",
          group.isUnassigned
            ? "bg-[color-mix(in_oklab,var(--avail-stretched)_45%,var(--card))]"
            : "bg-muted",
        )}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${group.label}`}
          onClick={() => onToggle(group.id)}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-sm outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronDown
            aria-hidden
            className={cn("size-3.5 text-heading transition-transform", !open && "-rotate-90")}
          />
        </button>
        <div className="flex min-w-0 flex-col gap-px">
          <span
            className={cn(
              "truncate font-display text-sm font-semibold",
              group.isUnassigned ? "text-[color:var(--avail-stretched-fg)]" : "text-heading",
            )}
          >
            {group.label}{" "}
            <span className="font-medium text-muted-foreground">({group.members.length})</span>
          </span>
          {notes.length > 0 ? (
            <span className="truncate text-xs text-muted-foreground">{notes.join(" · ")}</span>
          ) : null}
        </div>
      </div>
      <NetBar netHours={group.netHours} scale={GROUP_NET_SCALE} variant="group" />
      {layout.lastThreeMonths.length > 0 ? <LastThree items={lastThree} /> : null}
      {layout.columns.map((month, index) => {
        const result = group.cells[index];
        if (result === undefined) return <div key={month} />;
        return (
          <GroupCell
            key={month}
            group={group}
            month={month}
            result={result}
            gridRow={gridRow}
            gridCol={index}
            tabbable={tabCol === index}
            selected={selectedMonth === month}
            past={month < layout.currentMonth}
            edge={columnEdge(layout, month, index)}
            onSelect={onSelect}
            onTipShow={onTipShow}
            onTipHide={onTipHide}
          />
        );
      })}
    </div>
  );
});
